import { createContext, ReactNode, useContext } from 'react';
import { notification } from 'antd';

type NotificationType = 'success' | 'info' | 'warning' | 'error';

interface NotificationContextValue {
  showNotification: (
    type: NotificationType,
    message: string,
    description: string,
    duration?: number,
  ) => void;
}

const NotificationContext = createContext<NotificationContextValue | undefined>(
  undefined,
);

interface NotificationProviderProps {
  children: ReactNode;
}

const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
}) => {
  const [api, contextHolder] = notification.useNotification();

  const showNotification = (
    type: NotificationType,
    message: string,
    description: string,
    duration?: number,
  ) => {
    const isMultiLine = typeof description === 'string' && description.includes('\n');
    const defaultDuration = isMultiLine ? 7 : 4.5;
    api[type]({
      message,
      description: typeof description === 'string' ? (
        <span className="whitespace-pre-line text-xs">{description}</span>
      ) : (
        description
      ),
      duration: duration ?? defaultDuration,
    });
  };

  return (
    <NotificationContext.Provider value={{ showNotification }}>
      {contextHolder}
      {children}
    </NotificationContext.Provider>
  );
};

const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      'useNotification must be used within a NotificationProvider',
    );
  }
  return context;
};

export { NotificationProvider, useNotification };
